import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Waitlist() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist`, { email, name, company, role });
      if (res.data.already_exists) {
        toast.info(res.data.message);
      } else {
        toast.success(res.data.message);
      }
      setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 422) {
        toast.error('Please enter a valid email address.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="section-spacing scroll-mt-24"
    >
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left: Copy */}
          <div>
            <p data-animate className="kicker mb-5">Early Access</p>
            <h2
              data-animate
              style={{ transitionDelay: '70ms' }}
              className="font-display text-2xl sm:text-3xl lg:text-[2.5rem] tracking-[-0.01em] leading-[1] lg:leading-[1.1] mb-5"
            >
              Stop reviewing tenders<br />the hard way.
            </h2>
            <p
              data-animate
              style={{ transitionDelay: '140ms' }}
              className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6"
            >
              Join the waitlist for early access. Be first to transform how your
              team prepares, evaluates, and wins tenders.
            </p>
            <p
              data-animate
              style={{ transitionDelay: '200ms' }}
              className="text-[11px] text-muted-foreground/60"
            >
              No spam. No credit card required. Unsubscribe anytime.
            </p>
          </div>

          {/* Right: Application form */}
          <div data-animate style={{ transitionDelay: '150ms' }}>
            {submitted ? (
              <div
                data-testid="waitlist-success-message"
                className="flex items-center gap-3 py-8"
              >
                <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                <div>
                  <p className="text-base font-medium text-foreground">You're on the list!</p>
                  <p className="text-sm text-muted-foreground mt-1">We'll be in touch soon.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1.5 block">Full name</label>
                    <Input
                      data-testid="waitlist-name-input"
                      type="text"
                      placeholder="Jane Smith"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="h-11 bg-card border-border/70 focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground/40 rounded-xl text-sm"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1.5 block">Work email *</label>
                    <Input
                      data-testid="waitlist-email-input"
                      type="email"
                      placeholder="jane@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-11 bg-card border-border/70 focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground/40 rounded-xl text-sm"
                      disabled={loading}
                      required
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1.5 block">Company</label>
                    <Input
                      data-testid="waitlist-company-input"
                      type="text"
                      placeholder="Acme Corp"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      className="h-11 bg-card border-border/70 focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground/40 rounded-xl text-sm"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1.5 block">Role</label>
                    <Input
                      data-testid="waitlist-role-input"
                      type="text"
                      placeholder="Bid Manager"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      className="h-11 bg-card border-border/70 focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground/40 rounded-xl text-sm"
                      disabled={loading}
                    />
                  </div>
                </div>
                <Button
                  data-testid="waitlist-submit-button"
                  type="submit"
                  disabled={loading}
                  className="btn-lift bg-[hsl(150,18%,22%)] text-white hover:bg-[hsl(150,18%,26%)] h-12 px-8 text-sm font-medium rounded-full shadow-card w-full sm:w-auto mt-2"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Join the waitlist
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
